import { Component } from '@theme/component';

/**
 * @typedef {Object} RawStore
 * @property {string} name
 * @property {string} street
 * @property {string} postal_code
 * @property {string} city
 * @property {string} country
 * @property {string} url
 * @property {string} email
 * @property {string} phone
 * @property {string} latitude - Raw string from Liquid metaobject
 * @property {string} longitude - Raw string from Liquid metaobject
 */

/**
 * @typedef {Object} ValidatedStore
 * @property {string} name
 * @property {string} street
 * @property {string} postal_code
 * @property {string} city
 * @property {string} country
 * @property {string} url
 * @property {string} email
 * @property {string} phone
 * @property {string} latitude - Original string value
 * @property {string} longitude - Original string value
 * @property {number | null} lat - Parsed float or null if invalid
 * @property {number | null} lng - Parsed float or null if invalid
 * @property {boolean} hasValidCoords - True if lat/lng are within valid bounds
 */

/**
 * @typedef {Object} PaginationMeta
 * @property {number} current_page
 * @property {number} pages
 * @property {number} items
 * @property {number} page_size
 */

/**
 * Root web component for the store locator feature.
 *
 * Responsibilities:
 * 1. Read page-1 store data from embedded JSON in the DOM (zero network latency).
 * 2. Fetch page-2 stores via the Section Rendering API if pagination indicates more pages.
 * 3. Validate coordinates and partition stores into mapStores (valid coords) and listStores (all stores).
 * 4. Dispatch a `stores:ready` CustomEvent with { mapStores, listStores, total } for downstream sub-components.
 * 5. Cancel any in-flight fetch when the element is disconnected to prevent memory leaks.
 *
 * @extends {Component}
 */
class StoreLocator extends Component {
  /**
   * AbortController for cancelling in-flight page-2 fetch on disconnect.
   * @type {AbortController | null}
   */
  #abortController = null;

  /**
   * Called when the element is connected to the document's DOM.
   *
   * Synchronous per the Web Components spec — fires off async data loading without awaiting it.
   */
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('loading', '');
    this.#loadStores();

    // Toggle empty state attribute based on filter results.
    this.addEventListener('stores:filter', (event) => {
      const { total } = /** @type {CustomEvent} */ (event).detail;
      this.toggleAttribute('empty', total === 0);
    });

    // Delegated click handler — clears filters when the empty state button is clicked.
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.closest('[data-clear-filters]')) {
        this.dispatchEvent(new CustomEvent('stores:clear-filters', { bubbles: false }));
      }
    });
  }

  /**
   * Called when the element is disconnected from the document's DOM.
   *
   * Cancels any in-flight page-2 fetch to prevent memory leaks and orphaned network requests.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Orchestrates the full data loading pipeline:
   * reads embedded page-1 JSON, conditionally fetches page-2,
   * validates coordinates, and dispatches `stores:ready`.
   *
   * @returns {Promise<void>}
   */
  async #loadStores() {
    try {
      // 1. Read page-1 store data from embedded JSON script tag.
      const dataEl = this.querySelector('#store-locator-data-page-1');
      if (!dataEl) {
        throw new Error('Missing #store-locator-data-page-1 script tag — Liquid data layer not rendered.');
      }

      // 2. Read pagination metadata from embedded JSON script tag.
      const paginationEl = this.querySelector('#store-locator-pagination');
      if (!paginationEl) {
        throw new Error('Missing #store-locator-pagination script tag — Liquid pagination block not rendered.');
      }

      /** @type {RawStore[]} */
      let stores = JSON.parse(dataEl.textContent ?? '[]');

      /** @type {PaginationMeta} */
      const pagination = JSON.parse(paginationEl.textContent ?? '{}');

      // 3. Fetch page-2 if the dataset spans more than one page.
      if (pagination.pages > 1 && pagination.next_url) {
        const page2Stores = await this.#fetchPage2(this.dataset.sectionId ?? '', pagination.next_url);
        stores = [...stores, ...page2Stores];
      }

      // 4. Validate coordinates and partition into mapStores / listStores.
      const { mapStores, listStores } = this.#validateAndPartition(stores);

      // 5. Dispatch stores:ready for downstream sub-components (map, list).
      this.dispatchEvent(
        new CustomEvent('stores:ready', {
          bubbles: true,
          detail: { mapStores, listStores, total: stores.length },
        })
      );
      this.removeAttribute('loading');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      this.removeAttribute('loading');
      this.#handleError(/** @type {Error} */ (error));
    }
  }

  /**
   * Fetches page-2 store data via the Section Rendering API.
   *
   * Uses the `next_url` provided by Liquid's paginate object (guaranteed correct)
   * rather than constructing the URL manually. The API returns an HTML document
   * containing the full section markup, including a fresh `#store-locator-data-page-1`
   * script tag with the page-2 stores.
   *
   * @param {string} sectionId - The section ID from `data-section-id` on this element.
   * @param {string} nextUrl - The next page URL from Liquid's paginate.next.url.
   * @returns {Promise<RawStore[]>} The page-2 store objects.
   */
  async #fetchPage2(sectionId, nextUrl) {
    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    // Cancel any existing in-flight request before starting a new one.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const response = await fetch(url.toString(), { signal: this.#abortController.signal });

    if (!response.ok) {
      throw new Error(`Section Rendering API returned ${response.status} for page-2 fetch.`);
    }

    // Section Rendering API returns HTML, not JSON — parse with DOMParser.
    const html = await response.text();
    const fragment = new DOMParser().parseFromString(html, 'text/html');

    // The page-2 render uses the same script tag ID as page-1.
    const dataEl = fragment.getElementById('store-locator-data-page-1');
    if (!dataEl) {
      throw new Error('Page-2 response missing #store-locator-data-page-1 — Section Rendering API response malformed.');
    }

    return JSON.parse(dataEl.textContent ?? '[]');
  }

  /**
   * Validates store coordinates and partitions stores into two arrays:
   * - `mapStores`: stores with valid lat/lng, safe to place on a map.
   * - `listStores`: all stores, including those with invalid/missing coordinates.
   *
   * Metaobject field values arrive as strings from Liquid — we parse them here.
   * `hasValidCoords` is set on every store for downstream consumers (Phase 4 uses it
   * to render a visual warning indicator on invalid-coord store cards).
   *
   * @param {RawStore[]} rawStores
   * @returns {{ mapStores: ValidatedStore[], listStores: ValidatedStore[] }}
   */
  #validateAndPartition(rawStores) {
    /** @type {ValidatedStore[]} */
    const mapStores = [];
    /** @type {ValidatedStore[]} */
    const listStores = [];

    for (const store of rawStores) {
      const lat = parseFloat(store.latitude);
      const lng = parseFloat(store.longitude);

      const hasValidCoords =
        !isNaN(lat) &&
        !isNaN(lng) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180;

      // Normalize URL — metaobject values may lack a protocol prefix,
      // causing the browser to treat them as relative paths.
      let url = store.url;
      if (url && !/^https?:\/\//i.test(url)) {
        url = `https://${url}`;
      }

      /** @type {ValidatedStore} */
      const validated = {
        ...store,
        url,
        lat: hasValidCoords ? lat : null,
        lng: hasValidCoords ? lng : null,
        hasValidCoords,
      };

      // Every store is visible in the list.
      listStores.push(validated);

      // Only stores with valid coordinates appear on the map.
      if (hasValidCoords) {
        mapStores.push(validated);
      }
    }

    return { mapStores, listStores };
  }

  /**
   * Handles non-abort errors from the data loading pipeline.
   *
   * Error state UI is deferred to Phase 5 (UX-02). For now, errors are
   * surfaced in the console only with a consistent prefix for easy filtering.
   *
   * @param {Error} error
   */
  #handleError(error) {
    console.error('[Store Locator]', error.message);
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
